import "./SurveyCard.scss";

import {
  Alert,
  Button,
  ChevronDownIcon,
  ChevronStartIcon,
  ExclamationTriangleIcon,
  Flex,
  Text,
} from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import { getSurveyTypeDisplay } from "@teams/cards";
import { TeamsSurveyQnaResponse, TeamsSurveyState } from "@teams/types";
import { SurveyDTO, SurveyResponsesDTO } from "@teams/types/api";
import moment from "moment";
import React, { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { PollyIcon } from "../../../_shared/components/PollyIcon";
import { isError } from "../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../_shared/services/SurveyApi";
import { getEditInfoText } from "../../../_shared/utils/teamsHelpers";
import { HotTakeSurveyCardBody } from "./components/hotTake/HotTakeSurveyCardBody";
import { IcebreakerSurveyCardBody } from "./components/icebreaker/IcebreakerSurveyCardBody";
import { QnaSurveyCardBody } from "./components/qna/QnaSurveyCardBody";
import { SendSurveyButton } from "./components/SendSurveyButton";
import { SurveyInfoBar } from "./components/SurveyInfoBar";
import { TriviaSurveyCardBody } from "./components/trivia/TriviaSurveyCardBody";
import { useOnScreen } from "./hooks/useOnScreen";
import { SurveyCardBody } from "./SurveyCardBody";
import { SurveySideBarCardFooter } from "./SurveySideBarCardFooter";

type Props = {
  survey: SurveyDTO;
  teamsContext: app.Context;
  onChanged: () => void;
  onViewResults?: (survey: SurveyDTO, responses: SurveyResponsesDTO) => void;
  isSideBar?: boolean;
  position?: number;
  maxRespondents?: number;
};

export const SurveyCard: React.FC<Props> = ({
  survey,
  teamsContext,
  isSideBar,
  onChanged,
  onViewResults,
  maxRespondents,
}) => {
  const ref = useRef<HTMLDivElement>();
  const [surveyResponses, setSurveyResponses] = useState<SurveyResponsesDTO>({
    responses: [],
    canViewResponses: false,
  });
  const [hasLoadedResponses, setHasLoadedResponses] = useState<boolean>();
  const [surveyState, setSurveyState] = useState<TeamsSurveyState>(survey.state);
  const [isExpanded, setIsExpanded] = useState<boolean>(!isSideBar || survey.state === "open");
  const [error, setError] = useState<{ message: string; known?: boolean } | undefined>();
  const isVisible = useOnScreen(ref, "10px");
  const [hasUserResponded, setHasUserResponded] = useState<boolean>(false);

  const lastResponseTime = survey.responseUpdatedAt?.getTime();

  const loadResults = useCallback(async () => {
    const response = await surveyApi.surveyResponses(teamsContext, survey.id as string);
    setHasLoadedResponses(true);

    if (!isError(response)) {
      setHasUserResponded(!!response.userResponse);
      if (!response.canViewResponses) {
        switch (response.error) {
          case "AFTER-CLOSE":
            setError({
              message: `Results will be available after this ${getSurveyTypeDisplay(
                survey.type
              )} is closed`,
              known: true,
            });
            break;
          case "PRIVATE":
            setError({
              message: `Results only available to the owner of this ${getSurveyTypeDisplay(
                survey.type
              )}`,
              known: true,
            });
            break;
          case "FREE_TRIAL_RESPONSES_HIDDEN":
            setError({
              message: `Results are only available for 30 days on free plans`,
              known: true,
            });
            break;
          case "ACCESS_DENIED":
            setError({
              message: "Access denied to the results",
              known: false,
            });
            break;
          default:
            console.error(response);
            setError({ message: response.error || "Error loading results", known: false });
        }
      } else {
        setError(undefined);
        setSurveyResponses(response);
      }
    } else {
      console.error(response);
      setError(response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey.id]);

  useEffect(() => {
    if (!isVisible || surveyState === "pending" || surveyState === "deleted") {
      return;
    }

    loadResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, surveyState, lastResponseTime]);

  useEffect(() => {
    if (survey.state === "open" && !isExpanded) {
      setIsExpanded(true);
    }

    if (survey.state === "pending" && survey.deliverAt) {
      const futureDiff = moment().diff(moment(survey.deliverAt)) + 3000;
      const timeout = setTimeout(() => {
        onChanged();
      }, futureDiff);

      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyState]);

  useEffect(() => {
    if (survey.state !== surveyState) {
      setSurveyState(survey.state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey]);

  return (
    <div ref={ref as RefObject<HTMLDivElement>}>
      <Flex column className="survey-meeting-card">
        <div className="survey-meeting-card__top-border" />
        <Flex.Item grow>
          <Flex column gap="gap.medium" className="survey-meeting-card__body">
            <Flex gap="gap.small" column style={{ cursor: "pointer" }}>
              <SurveyInfoBar
                survey={survey}
                onChanged={onChanged}
                isSideBar={isSideBar}
                hasUserResponded={hasUserResponded}
              />
              <Flex gap="gap.small" onClick={() => setIsExpanded(!isExpanded)}>
                <Flex.Item grow>
                  <Text content={survey.title} weight="bold" size="medium" as="div" />
                </Flex.Item>

                <Flex.Item push>
                  <span>
                    {isExpanded && <ChevronDownIcon size="small" />}
                    {!isExpanded && <ChevronStartIcon size="small" />}
                  </span>
                </Flex.Item>
              </Flex>
            </Flex>

            {survey.appealInfoType && (
              <Text
                className="survey-meeting-card__info-text"
                content={getEditInfoText(survey?.appealInfoType)}
              />
            )}

            {isExpanded && (
              <Flex column gap="gap.small" className="survey-meeting-card__body-content">
                <CardBody
                  survey={survey}
                  surveyResponses={surveyResponses}
                  onViewResults={onViewResults}
                  hasLoadedResponses={hasLoadedResponses}
                  isSideBar={isSideBar}
                  maxRespondents={maxRespondents}
                />
                {error &&
                  (!error!.known ? (
                    <Alert
                      icon={<ExclamationTriangleIcon />}
                      warning={error.known}
                      danger={!error.known}
                      content={error.message}
                    />
                  ) : (
                    <Flex vAlign="start" gap="gap.small" className="warning-container">
                      <PollyIcon icon="info-icon" inheritColor />
                      <Text content={error.message} styles={{ marginTop: "-4px" }} />
                    </Flex>
                  ))}
              </Flex>
            )}
          </Flex>
        </Flex.Item>

        <Flex.Item push>
          <Flex column className="survey-meeting-card__footer">
            {isSideBar && (
              <SurveySideBarCardFooter
                teamsContext={teamsContext}
                survey={survey}
                userHasResponded={!!surveyResponses.userResponse}
                onChanged={(newSurveyState?: TeamsSurveyState) => {
                  if (newSurveyState) {
                    setSurveyState(newSurveyState);
                  }
                  setIsExpanded(true);
                  onChanged();
                }}
                onResponded={() => {
                  loadResults();
                }}
              />
            )}
            {!isSideBar && (
              <Flex space="between" vAlign="end">
                <Flex.Item push>
                  <Flex>
                    {survey.role === "own" && surveyState === "pending" && !survey.deliverAt && (
                      <>
                        <SendSurveyButton
                          survey={survey}
                          teamsContext={teamsContext}
                          onChanged={(newSurveyState?: TeamsSurveyState) => {
                            if (newSurveyState) {
                              setSurveyState(newSurveyState);
                            }
                            setError(undefined);
                            onChanged();
                          }}
                          onError={errorMessage => {
                            setError({ message: errorMessage, known: false });
                          }}
                        />
                      </>
                    )}
                  </Flex>
                </Flex.Item>
              </Flex>
            )}
          </Flex>
        </Flex.Item>

        {survey.type === "qna" && survey.state !== "pending" && onViewResults && (
          <Flex className="survey-meeting-card__footer">
            <Button
              content={survey.state === "open" ? "Join Q&A" : "View submission"}
              fluid
              secondary
              onClick={() => onViewResults(survey, surveyResponses)}
            />
          </Flex>
        )}
      </Flex>
    </div>
  );
};

const CardBody: React.FC<{
  survey: SurveyDTO;
  surveyResponses: SurveyResponsesDTO;
  onViewResults?: (survey: SurveyDTO, responses: SurveyResponsesDTO) => void;
  hasLoadedResponses?: boolean;
  isSideBar?: boolean;
  maxRespondents?: number;
}> = ({
  survey,
  surveyResponses,
  onViewResults,
  hasLoadedResponses,
  isSideBar,
  maxRespondents,
}) => {
  switch (survey.type) {
    case "trivia":
      return (
        <TriviaSurveyCardBody
          survey={survey}
          surveyResponses={surveyResponses}
          onViewResults={onViewResults}
        />
      );

    case "qna":
      return (
        <QnaSurveyCardBody
          survey={survey}
          responses={surveyResponses.responses as TeamsSurveyQnaResponse[]}
        />
      );

    case "icebreaker":
      return (
        <IcebreakerSurveyCardBody
          survey={survey}
          hasLoadedResponses={hasLoadedResponses}
          surveyResponses={surveyResponses}
          isSideBar={isSideBar}
          onViewResults={onViewResults}
        />
      );

    case "hot-take":
      return (
        <HotTakeSurveyCardBody
          survey={survey}
          hasLoadedResponses={hasLoadedResponses}
          surveyResponses={surveyResponses}
          isSideBar={isSideBar}
          onViewResults={onViewResults}
        />
      );

    default:
      return (
        <SurveyCardBody
          survey={survey}
          hasLoadedResponses={hasLoadedResponses}
          surveyResponses={surveyResponses}
          isSideBar={isSideBar}
          maxRespondents={maxRespondents}
          onViewResults={onViewResults}
        />
      );
  }
};
